import { FC } from 'react';

import AppStoreQRCode from '../assets/app-store-qr-code.svg';
import ExternalLinkIcon from '../assets/li_external-link.svg';
import { useMergedConfig } from '../context/MergedConfigProvider';
import { useTranslations } from '../hooks/useTranslations';
import { translationKeys } from '../translations/translationKeys';
import logger from '../utils/logger.ts';
import { setPlatformLanguage, setPlatformUrlParam } from '../utils/platformUrl.ts';
import { useIsMobile } from '../utils/useIsMobile';
import { useToast } from './Toast';

const PLATFORM_LINK = import.meta.env.VITE_PLATFORM_URL;
const APP_ONE_LINK = import.meta.env.VITE_APP_ONE_LINK;
const APP_STORE_LINK = import.meta.env.VITE_APP_STORE_LINK;
const PLAY_STORE_LINK = import.meta.env.VITE_PLAY_STORE_LINK;
const FALLBACK_HREF = `${APP_STORE_LINK} OR ${PLAY_STORE_LINK}`;

type Props = {
  embedded: boolean;
  context?: 'Live' | 'Demo';
};

const ThankYouContent: FC<Props> = ({ embedded, context = 'Live' }) => {
  const isMobile = useIsMobile();
  const { translate } = useTranslations();
  const { showToast, ToastComponent } = useToast();
  const { launchedInNewTab, lang, region } = useMergedConfig();
  let platformLink = setPlatformLanguage(PLATFORM_LINK, lang);
  if (region) {
    platformLink = setPlatformUrlParam(platformLink, 'r', region);
  }
  const isLive = context === 'Live';

  const handleCopyLink = () => {
    try {
      navigator.clipboard.writeText(platformLink);
      showToast('Link copied to clipboard');
    } catch (error) {
      showToast('Could not copy link');
      logger.error('Could not copy link: ' + (error instanceof Error ? error.message : String(error)));
    }
  };

  return (
    <div>
      {ToastComponent}

      {isLive && (
        <div className="w-full mb-6">
          <p className="text-base leading-6 text-content-secondary">
            {translate(translationKeys.THANK_YOU_REVIEWING_BODY)}
          </p>
        </div>
      )}

      {launchedInNewTab && (
        <div className="w-full mb-6">
          <p className="text-base leading-6 text-content-secondary">
            {translate(translationKeys.THANK_YOU_CLOSE_TAB_BODY)}
          </p>
        </div>
      )}

      {!embedded && !launchedInNewTab && isLive && (
        <div className="w-full mb-6">
          <p className="text-base leading-6 text-content-secondary">
            <span className="font-bold">{translate(translationKeys.THANK_YOU_DEMO_READY_HEADING)}</span>
            <br />
            {translate(translationKeys.THANK_YOU_DEMO_READY_LOGIN_LIVE)}
          </p>
        </div>
      )}

      {!embedded && !launchedInNewTab && !isLive && (
        <div className="w-full mb-6">
          <p className="text-base leading-6 text-content-secondary">
            {translate(translationKeys.THANK_YOU_DEMO_READY_LOGIN_DEMO)}
          </p>
        </div>
      )}

      {!embedded &&
        !launchedInNewTab &&
        (isMobile ? (
          <div className="w-full flex items-center py-6">
            <button onClick={handleCopyLink} className="text-blue-400 inline-flex items-center gap-1 cursor-pointer">
              {translate(translationKeys.THANK_YOU_COPY_LINK)}
              <img src={ExternalLinkIcon} alt="" className="inline-block w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="w-full flex flex-col gap-2 py-6">
            <div className="bg-surface-separator flex gap-6 items-center p-3 w-full">
              <a
                id="qr-code"
                href={APP_ONE_LINK}
                data-fallback-href={FALLBACK_HREF}
                data-onelink-href={APP_ONE_LINK}
                className="w-[55px] shrink-0 bg-white"
              >
                <img src={AppStoreQRCode} alt="Download app QR code" className="w-full h-full" />
              </a>
              <div className="flex-1">
                <p className="text-sm leading-5 text-content-secondary">
                  {translate(translationKeys.THANK_YOU_QR_CODE_HEADING)}
                </p>
              </div>
            </div>

            <div className="text-center">
              <p className="text-xs leading-4 text-content-secondary">
                Available on&nbsp;
                <a
                  href={APP_ONE_LINK}
                  data-fallback-href={APP_STORE_LINK}
                  data-onelink-href={APP_ONE_LINK}
                  className="text-blue-400 underline"
                >
                  App Store
                </a>
                <span className="text-blue-400 text-[10px] align-top pl-0.5">®</span>
                &nbsp;and&nbsp;
                <a
                  href={APP_ONE_LINK}
                  data-fallback-href={PLAY_STORE_LINK}
                  data-onelink-href={APP_ONE_LINK}
                  className="text-blue-400 underline"
                >
                  Google Play
                </a>
              </p>
            </div>
          </div>
        ))}
    </div>
  );
};

export default ThankYouContent;
